"use client";

import { useState } from "react";
import { ChevronDown, Send } from "lucide-react";
import posthog from "posthog-js";

import { Button } from "@/components/ui/Button";
import { contact } from "@/lib/data";

const SUBJECTS = [
  "Parceria comercial",
  "Uso empresarial do Zenthor",
  "Imprensa e comunicação",
  "Suporte ao produto Zenthor",
  "Privacidade e proteção de dados",
  "Outro assunto",
] as const;

type Subject = (typeof SUBJECTS)[number];

const PROFILES = ["Pessoa física", "Empresa"] as const;
type Profile = (typeof PROFILES)[number];

const inputClass =
  "w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-ink placeholder:text-mist/50 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20";

// VOR-96 (2026-05-10) — antes o form usava `noValidate`, o que permitia
// envio mesmo com campos obrigatórios em branco. Removido. Agora cada
// `required` é respeitado pelo navegador, e adicionamos uma checagem
// extra antes de abrir o mailto pra garantir que e-mail/assunto/perfil/
// mensagem não venham vazios mesmo sob bypass de validação HTML5.
//
// VOR-95 (2026-05-10) — Bea pediu:
//   - radios "Pessoa física | Empresa" abaixo do e-mail (`profile`)
//   - refinar tela de sucesso (estava confusa pós-envio)

export function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [profile, setProfile] = useState<Profile | "">("");
  const [subject, setSubject] = useState<Subject | "">("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Trim + checagem dura antes de redirecionar (VOR-96).
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();
    if (!trimmedName || !trimmedEmail || !profile || !subject || !trimmedMessage) {
      return;
    }

    if (process.env.NEXT_PUBLIC_POSTHOG_TOKEN) {
      posthog.capture("institutional_contact_submitted", {
        subject,
        profile,
        delivery: "mailto",
      });
    }

    const body = encodeURIComponent(
      `Nome: ${trimmedName}\nPerfil: ${profile}\nAssunto: ${subject}\n\n${trimmedMessage}`
    );
    window.location.href = `mailto:${contact.email}?subject=${encodeURIComponent(subject)}&body=${body}`;
    setSent(true);
  };

  if (sent) {
    return (
      <div className="panel mt-10 p-8 text-center">
        <h2 className="font-display text-2xl font-medium text-white">
          Mensagem preparada
        </h2>
        <p className="mt-3 text-sm leading-7 text-mist">
          Abrimos seu cliente de e-mail com os dados preenchidos. Basta
          revisar e clicar em enviar.
        </p>
        <p className="mt-2 text-sm leading-7 text-mist">
          Se preferir, copie nosso e-mail:{" "}
          <a
            href={`mailto:${contact.email}`}
            className="text-accent transition hover:underline"
          >
            {contact.email}
          </a>
          .
        </p>
        <div className="mt-6">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setSent(false);
              setName("");
              setEmail("");
              setProfile("");
              setSubject("");
              setMessage("");
            }}
          >
            Enviar outra mensagem
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="panel mt-10 space-y-5 p-6 sm:p-8">
      <div>
        <label htmlFor="name" className="mb-2 block text-sm font-medium text-ink">
          Nome <span className="text-accent">*</span>
        </label>
        <input
          id="name"
          type="text"
          required
          placeholder="Seu nome"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputClass}
          autoComplete="name"
        />
      </div>

      <div>
        <label htmlFor="email" className="mb-2 block text-sm font-medium text-ink">
          E-mail <span className="text-accent">*</span>
        </label>
        <input
          id="email"
          type="email"
          required
          placeholder="seu@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
          autoComplete="email"
        />
      </div>

      <fieldset>
        <legend className="mb-2 block text-sm font-medium text-ink">
          Você é <span className="text-accent">*</span>
        </legend>
        <div className="flex flex-wrap gap-3">
          {PROFILES.map((option) => {
            const id = `profile-${option}`;
            const checked = profile === option;
            return (
              <label
                key={option}
                htmlFor={id}
                className={`flex cursor-pointer items-center gap-2 rounded-2xl border px-4 py-2.5 text-sm transition ${
                  checked
                    ? "border-accent bg-accent/10 text-white"
                    : "border-white/10 bg-white/5 text-mist hover:border-white/20"
                }`}
              >
                <input
                  id={id}
                  type="radio"
                  name="profile"
                  value={option}
                  required
                  checked={checked}
                  onChange={() => setProfile(option)}
                  className="sr-only"
                />
                <span
                  aria-hidden="true"
                  className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                    checked ? "border-accent" : "border-white/30"
                  }`}
                >
                  {checked ? <span className="h-2 w-2 rounded-full bg-accent" /> : null}
                </span>
                {option}
              </label>
            );
          })}
        </div>
      </fieldset>

      <div>
        <label htmlFor="subject" className="mb-2 block text-sm font-medium text-ink">
          Assunto <span className="text-accent">*</span>
        </label>
        <div className="relative">
          <select
            id="subject"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value as Subject)}
            className={`${inputClass} appearance-none pr-10`}
          >
            <option value="" disabled className="bg-canvas text-mist">
              Selecione o assunto
            </option>
            {SUBJECTS.map((s) => (
              <option key={s} value={s} className="bg-canvas text-ink">
                {s}
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mist"
            aria-hidden="true"
          />
        </div>
      </div>

      <div>
        <label htmlFor="message" className="mb-2 block text-sm font-medium text-ink">
          Mensagem <span className="text-accent">*</span>
        </label>
        <textarea
          id="message"
          required
          rows={5}
          placeholder="Descreva seu projeto, dúvida ou proposta..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={`${inputClass} resize-none`}
        />
      </div>

      <Button type="submit" className="gap-2">
        <Send className="h-4 w-4" aria-hidden="true" />
        Enviar mensagem
      </Button>
    </form>
  );
}
